/**
 * NewCustomerOrderModal — Feb 2026
 * ------------------------------------------------------------------
 * Customer-order creation for admins with the `orders.create`
 * permission (also super admin). Mirrors the public /api/orders
 * shape but is driven from the Admin Orders page — so ops can cut
 * a customer order on behalf of an existing buyer without asking
 * the customer to log in.
 *
 *   1. Pick a customer (search by name / email / phone / cnum /
 *      business).
 *   2. Add items (search by SKU / item code / name → click to add →
 *      set qty). In-hand chip guards against oversell.
 *   3. Submit.
 *
 * On submit we POST to `/api/admin/orders` WITHOUT the branch flags
 * so `admin_create_order` writes it as a customer order
 * (`order_type: "customer"`) using the selected customer's profile
 * (name / email / phone / address). The order then flows through
 * the standard confirm → FEFO → dispatch pipeline exactly like a
 * customer-placed one.
 */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Plus, Search, X, User, Package, Upload, FileDown, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import CustomerNameLabel from './CustomerNameLabel';
import { customerSingleLine, customerPrimaryLabel } from '../lib/customerName';

const priceOf = (p) => Number(p?.price ?? p?.base_price ?? 0) || 0;
const availableOf = (p) => {
  if (!p) return 0;
  const v = (p.available_qty !== undefined && p.available_qty !== null)
    ? p.available_qty
    : p.stock_qty;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};
const customerLabel = (c) => customerSingleLine(c);

const NewCustomerOrderModal = ({ customers, products, onClose, onCreated, authHeaders }) => {
  const { toast } = useToast();
  const [customerQuery, setCustomerQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [productQuery, setProductQuery] = useState('');
  const [cart, setCart] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Excel upload state (Feb 2026) — pre-fills the cart from a two-column
  // Item Code / Qty workbook. Preview-and-confirm; invalid rows are
  // highlighted and auto-skipped on confirm. All existing order-creation
  // logic is unchanged — this ONLY populates `cart`.
  const excelInputRef = useRef(null);
  const [excelError, setExcelError] = useState('');
  const [excelPreview, setExcelPreview] = useState(null);

  const customerList = Array.isArray(customers) ? customers : [];

  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return customerList.slice(0, 30);
    return customerList.filter((c) => (
      (c.name || c.full_name || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q)
      || (c.phone || '').toLowerCase().includes(q)
      || (c.business_name || '').toLowerCase().includes(q)
      || String(c.customer_number || '').toLowerCase().includes(q)
    )).slice(0, 30);
  }, [customerQuery, customerList]);

  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products.slice(0, 30);
    return products.filter((p) => (
      (p.sku || '').toLowerCase().includes(q)
      || (p.name || '').toLowerCase().includes(q)
      || (p.item_code || '').toLowerCase().includes(q)
    )).slice(0, 30);
  }, [productQuery, products]);

  const productsById = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  const cartRows = Object.entries(cart)
    .map(([pid, qty]) => ({ product: productsById[pid], qty: Number(qty) }))
    .filter((r) => r.product && r.qty >= 0);

  const cartRowsWithAvail = cartRows.map((r) => {
    const avail = availableOf(r.product);
    return { ...r, avail, over: r.qty > avail };
  });
  const hasOverCap = cartRowsWithAvail.some((r) => r.over);
  const hasAnyValidQty = cartRows.some((r) => r.qty > 0);
  const subtotal = cartRows.reduce((acc, r) => acc + priceOf(r.product) * r.qty, 0);

  const addToCart = (product) => {
    setCart((prev) => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));
  };

  // Feb 2026 — Allow multi-digit manual entry (see NewExportOrderModal
  // for full rationale). Accept raw text, keep row alive at 0 during
  // in-progress typing, remove only when blur commits.
  const setQty = (pid, qty) => {
    const raw = String(qty ?? '').replace(/[^0-9]/g, '');
    if (raw === '') {
      setCart((prev) => ({ ...prev, [pid]: 0 }));
      return;
    }
    const n = Math.max(0, parseInt(raw, 10));
    setCart((prev) => ({ ...prev, [pid]: n }));
  };
  const commitQty = (pid) => {
    setCart((prev) => {
      const cur = prev[pid];
      if (!cur || cur <= 0) {
        const next = { ...prev };
        delete next[pid];
        return next;
      }
      return prev;
    });
  };
  const removeFromCart = (pid) => setCart((prev) => {
    const next = { ...prev };
    delete next[pid];
    return next;
  });

  // --- Excel upload -------------------------------------------------
  // Build a case-insensitive lookup from both `sku` and `item_code` to
  // the matching product. Memoised so parse() can match in O(1) per row.
  const productByCode = useMemo(() => {
    const map = {};
    (products || []).forEach((p) => {
      [p.sku, p.item_code].forEach((raw) => {
        if (raw === undefined || raw === null) return;
        const k = String(raw).trim().toLowerCase();
        if (k) map[k] = p;
      });
    });
    return map;
  }, [products]);

  const downloadExcelTemplate = () => {
    // Two-column template with a few blank rows + two example rows so the
    // header shape is unambiguous for the ops user.
    const data = [
      ['Item Code', 'Qty'],
      ['', ''],
      ['', ''],
      ['', ''],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    // Nicer column widths so the header isn't squashed in Excel/Numbers.
    ws['!cols'] = [{ wch: 24 }, { wch: 10 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Order');
    XLSX.writeFile(wb, 'direct-order-template.xlsx');
  };

  const parseExcelFile = async (file) => {
    setExcelError('');
    setExcelPreview(null);
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) {
        setExcelError('No sheet found in the workbook.');
        return;
      }
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      if (rows.length === 0) {
        setExcelError('The sheet is empty — add rows under the Item Code / Qty header.');
        return;
      }
      // Resolve the two expected headers case-insensitively, tolerating
      // "Item Code" / "ItemCode" / "SKU" / "Qty" / "Quantity".
      const headerKeys = Object.keys(rows[0]);
      const codeKey = headerKeys.find((k) => /^(item\s*code|sku)$/i.test(String(k).trim()));
      const qtyKey = headerKeys.find((k) => /^(qty|quantity)$/i.test(String(k).trim()));
      if (!codeKey || !qtyKey) {
        setExcelError(
          'Required columns not found. Expected a header row with "Item Code" and "Qty".',
        );
        return;
      }
      // Merge rows by Item Code BEFORE recording errors so an entry only
      // becomes invalid when every occurrence of that code is invalid.
      const merged = new Map();
      rows.forEach((raw, i) => {
        const rowNum = i + 2; // +1 for 1-based, +1 for header row
        const rawCode = String(raw[codeKey] ?? '').trim();
        const rawQty = raw[qtyKey];
        // Skip fully-blank rows silently so a tail of empty cells doesn't
        // flood the preview with errors.
        if (!rawCode && (rawQty === '' || rawQty === null || rawQty === undefined)) {
          return;
        }
        const key = rawCode.toLowerCase();
        const num = Number(rawQty);
        const qty = Number.isFinite(num) ? Math.floor(num) : NaN;
        let error = '';
        let product = null;
        if (!rawCode) {
          error = 'Item Code is empty';
        } else {
          product = productByCode[key] || null;
          if (!product) error = 'Item Code not found in product catalog';
        }
        if (!error) {
          if (!Number.isFinite(num)) error = 'Qty is not a valid number';
          else if (qty <= 0) error = 'Qty must be greater than 0';
        }
        const bucketKey = key || `__blank-${rowNum}`;
        const existing = merged.get(bucketKey);
        if (existing && !existing.error && !error) {
          existing.qty += qty;
          existing.mergedRows.push(rowNum);
        } else if (!existing) {
          merged.set(bucketKey, {
            rowNum,
            itemCode: rawCode,
            qty: Number.isFinite(num) ? qty : rawQty,
            product,
            error,
            mergedRows: [rowNum],
          });
        }
        // If a later row for the same code is valid but the first was
        // flagged (e.g. first row had a bad qty), keep the first entry
        // intact so the operator can see the original mistake — the spec
        // says "merge same Item Code", not "retry on duplicates".
      });
      const list = Array.from(merged.values());
      if (list.length === 0) {
        setExcelError('No data rows found below the header.');
        return;
      }
      const validCount = list.filter((x) => !x.error).length;
      setExcelPreview({
        fileName: file.name,
        rows: list,
        validCount,
        invalidCount: list.length - validCount,
      });
    } catch (err) {
      setExcelError(err?.message || 'Could not read the Excel file.');
    }
  };

  const confirmExcelImport = () => {
    if (!excelPreview) return;
    const valid = excelPreview.rows.filter((x) => !x.error && x.product);
    if (valid.length === 0) {
      toast({
        title: 'Nothing to add',
        description: 'All rows in this file are invalid.',
      });
      setExcelPreview(null);
      return;
    }
    setCart((prev) => {
      const next = { ...prev };
      valid.forEach(({ product, qty }) => {
        next[product.id] = (Number(next[product.id]) || 0) + Number(qty);
      });
      return next;
    });
    const added = valid.length;
    const skipped = excelPreview.invalidCount;
    toast({
      title: `Added ${added} item${added === 1 ? '' : 's'} from Excel`,
      description:
        skipped > 0
          ? `${skipped} invalid row${skipped === 1 ? '' : 's'} skipped.`
          : undefined,
    });
    setExcelPreview(null);
    setExcelError('');
    if (excelInputRef.current) excelInputRef.current.value = '';
  };

  const cancelExcelImport = () => {
    setExcelPreview(null);
    setExcelError('');
    if (excelInputRef.current) excelInputRef.current.value = '';
  };

  const canSubmit = !!selectedCustomer && hasAnyValidQty && !submitting && !hasOverCap;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const items = cartRows.filter((r) => r.qty > 0).map((r) => ({
        id: r.product.id,
        name: r.product.name,
        sku: r.product.sku || r.product.item_code || '',
        size: r.product.size || '',
        size_type: r.product.size_type || 'size',
        price: priceOf(r.product),
        qty: r.qty,
        image: r.product.image || '',
        vat_applicable: !!r.product.vat_applicable,
        uom: r.product.uom || 'ctn',
      }));
      const c = selectedCustomer;
      const body = {
        // NO branch_account_id / order_type → server writes it as a
        // customer order following the same workflow as public
        // /api/orders submissions.
        customer_name: c.name || c.full_name || customerLabel(c),
        customer_phone: c.phone || '',
        customer_email: c.email || '',
        customer_address: c.address || '',
        customer_business_name: c.business_name || '',
        notes: '',
        items,
        status: 'confirmed',
      };
      const { data } = await axios.post(`${API}/admin/orders`, body, { headers: authHeaders });
      toast({
        title: 'Customer order created',
        description: `${data?.order_number || ''} · ${customerLabel(c)} · ${items.length} item${items.length === 1 ? '' : 's'}`,
      });
      onCreated?.(data);
    } catch (e) {
      toast({
        title: 'Order create failed',
        description: e.response?.data?.detail || e.message || 'Try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex md:items-center md:justify-center md:p-4"
      data-testid="new-customer-order-modal"
    >
      <div className="bg-white w-full h-full md:h-auto md:rounded-2xl md:max-w-2xl md:max-h-[90vh] flex flex-col">
        <div className="p-4 md:p-5 border-b border-neutral-100 flex items-center justify-between gap-4 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">New Customer Order</div>
            <div className="font-display text-base md:text-lg font-bold text-[#0F172A] truncate">
              {selectedCustomer ? customerLabel(selectedCustomer) : 'Pick a customer to start'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 p-1"
            data-testid="new-customer-order-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 pb-28 md:pb-5">
          {/* Step 1: pick a customer */}
          <section data-testid="new-customer-order-customer-section">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5">
                <User className="w-3.5 h-3.5" /> Customer
              </div>
              {selectedCustomer && (
                <button
                  type="button"
                  onClick={() => setSelectedCustomer(null)}
                  className="text-[11px] font-semibold text-[#0078D4] hover:underline"
                  data-testid="new-customer-order-change-customer-btn"
                >
                  Change
                </button>
              )}
            </div>
            {selectedCustomer ? (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-3"
                data-testid="new-customer-order-selected-customer"
              >
                <User className="w-5 h-5 text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-emerald-900">
                    {customerLabel(selectedCustomer)}
                  </div>
                  <div className="text-[11px] text-emerald-800/80 truncate">
                    {selectedCustomer.email && <span className="mr-2">{selectedCustomer.email}</span>}
                    {selectedCustomer.phone && <span className="mr-2">· {selectedCustomer.phone}</span>}
                    {selectedCustomer.customer_number && (
                      <span>· cnum <span className="font-mono">{selectedCustomer.customer_number}</span></span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Search by name, business, email, phone or customer #"
                    className="w-full text-sm border border-neutral-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#0078D4]"
                    data-testid="new-customer-order-customer-search"
                  />
                </div>
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="new-customer-order-customer-results">
                  {filteredCustomers.length === 0 && (
                    <div className="p-3 text-xs text-neutral-400">
                      {customerList.length === 0
                        ? 'No customers yet — add one in Admin → Customers.'
                        : 'No customers match — try a different search.'}
                    </div>
                  )}
                  {filteredCustomers.map((c) => (
                    <button
                      key={c.email || c.id}
                      type="button"
                      onClick={() => setSelectedCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2"
                      data-testid={`new-customer-order-customer-option-${c.email || c.id}`}
                    >
                      <User className="w-4 h-4 text-neutral-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <CustomerNameLabel customer={c} className="truncate" />
                        <div className="text-[11px] text-neutral-500 truncate mt-1">
                          {c.email && <span className="mr-2">{c.email}</span>}
                          {c.phone && <span className="mr-2">· {c.phone}</span>}
                          {c.customer_number && (
                            <span>· <span className="font-mono">{c.customer_number}</span></span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>

          {/* Step 2: items — unlocked once a customer is picked */}
          {selectedCustomer && (
            <section data-testid="new-customer-order-items-section">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" /> Items ({cartRows.length})
                </div>
                <div className="flex items-center gap-1.5" data-testid="new-customer-order-excel-actions">
                  <button
                    type="button"
                    onClick={downloadExcelTemplate}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-600 hover:text-[#0078D4] border border-neutral-200 hover:border-[#0078D4] bg-white rounded-full px-2.5 py-1"
                    data-testid="new-customer-order-excel-template-btn"
                    title="Download a blank Item Code / Qty template"
                  >
                    <FileDown className="w-3.5 h-3.5" /> Template
                  </button>
                  <button
                    type="button"
                    onClick={() => excelInputRef.current?.click()}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-[#0078D4] hover:bg-[#005A9E] rounded-full px-2.5 py-1"
                    data-testid="new-customer-order-excel-upload-btn"
                    title="Upload a filled-in Item Code / Qty Excel file"
                  >
                    <Upload className="w-3.5 h-3.5" /> Upload Excel
                  </button>
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) parseExcelFile(f);
                      // Reset so re-uploading the SAME file still fires onChange.
                      e.target.value = '';
                    }}
                    data-testid="new-customer-order-excel-file-input"
                  />
                </div>
              </div>
              {excelError && (
                <div
                  className="mb-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5 flex items-start gap-1.5"
                  data-testid="new-customer-order-excel-error"
                >
                  <AlertCircle className="w-3.5 h-3.5 mt-[1px] shrink-0" />
                  <span>{excelError}</span>
                </div>
              )}
              <div className="relative">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Search by item code (SKU) or name"
                  className="w-full text-sm border border-neutral-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#0078D4]"
                  data-testid="new-customer-order-item-search"
                />
              </div>
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="new-customer-order-item-results">
                {filteredProducts.length === 0 && (
                  <div className="p-3 text-xs text-neutral-400">No matching products — check the item code or clear the search.</div>
                )}
                {filteredProducts.map((p) => {
                  const avail = availableOf(p);
                  const inStock = avail > 0;
                  const rowQty = cart[p.id] || 0;
                  return (
                    <div
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => addToCart(p)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addToCart(p); } }}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2 cursor-pointer"
                      data-testid={`new-customer-order-item-option-${p.id}`}
                    >
                      <Plus className="w-4 h-4 text-[#0078D4] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A] truncate">
                          {p.name}
                          {p.sku && <span className="ml-2 font-mono text-[10px] text-neutral-500">{p.sku}</span>}
                          <span
                            className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                            data-testid={`new-customer-order-item-inhand-${p.id}`}
                          >
                            In hand: {avail.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-[11px] text-neutral-500 tabular-nums">
                          OMR {priceOf(p).toFixed(3)} / {p.uom || 'ctn'}
                          {rowQty > 0 && (
                            <span className="ml-2 text-emerald-700 font-semibold">In cart: {rowQty}</span>
                          )}
                        </div>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={rowQty === 0 ? '' : String(rowQty)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => { e.stopPropagation(); e.target.select(); }}
                        onChange={(e) => { e.stopPropagation(); setQty(p.id, e.target.value); }}
                        onBlur={(e) => { e.stopPropagation(); commitQty(p.id); }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') e.target.blur();
                        }}
                        className="w-16 shrink-0 text-sm text-center border border-neutral-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#0078D4] tabular-nums font-semibold"
                        data-testid={`new-customer-order-picker-qty-${p.id}`}
                        placeholder="qty"
                        aria-label={`Quantity for ${p.name}`}
                      />
                    </div>
                  );
                })}
              </div>

              {cartRows.length > 0 && (
                <div className="mt-4 rounded-xl border border-neutral-200 divide-y divide-neutral-100" data-testid="new-customer-order-cart">
                  {cartRowsWithAvail.map((r) => (
                    <div
                      key={r.product.id}
                      className={`p-3 flex items-center gap-3 ${r.over ? 'bg-red-50/40' : ''}`}
                      data-testid={`new-customer-order-cart-row-${r.product.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-[#0F172A] truncate">
                          {r.product.name}
                          {r.product.sku && <span className="ml-2 font-mono text-[10px] text-neutral-500">{r.product.sku}</span>}
                          <span
                            className={`ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${r.avail > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                            data-testid={`new-customer-order-cart-inhand-${r.product.id}`}
                          >
                            In hand: {r.avail.toLocaleString()}
                          </span>
                        </div>
                        <div className="text-[11px] text-neutral-500 tabular-nums">
                          OMR {priceOf(r.product).toFixed(3)} × {r.qty} = <span className="font-bold text-[#0F172A]">OMR {(priceOf(r.product) * r.qty).toFixed(3)}</span>
                        </div>
                        {r.over && (
                          <div
                            className="mt-1 text-[11px] font-semibold text-red-700 flex items-center gap-1"
                            data-testid={`new-customer-order-cart-overcap-${r.product.id}`}
                          >
                            <span aria-hidden>⚠</span>
                            {r.avail > 0
                              ? `Exceeds in-hand stock — max available: ${r.avail.toLocaleString()}`
                              : 'Out of stock — nothing available to allocate'}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setQty(r.product.id, Math.max(1, r.qty - 1))}
                          className="w-7 h-8 rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 flex items-center justify-center"
                          data-testid={`new-customer-order-cart-qty-dec-${r.product.id}`}
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={r.qty === 0 ? '' : String(r.qty)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setQty(r.product.id, e.target.value)}
                          onBlur={() => commitQty(r.product.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          className={`w-20 text-sm text-center border rounded-lg px-2 py-1 focus:outline-none tabular-nums font-semibold ${r.over ? 'border-red-400 bg-red-50 focus:border-red-500' : 'border-neutral-200 focus:border-[#0078D4]'}`}
                          data-testid={`new-customer-order-cart-qty-${r.product.id}`}
                          placeholder="qty"
                        />
                        <button
                          type="button"
                          onClick={() => setQty(r.product.id, r.qty + 1)}
                          className="w-7 h-8 rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 flex items-center justify-center"
                          data-testid={`new-customer-order-cart-qty-inc-${r.product.id}`}
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFromCart(r.product.id)}
                        className="text-neutral-400 hover:text-red-600 p-1"
                        data-testid={`new-customer-order-cart-remove-${r.product.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="p-3 bg-neutral-50 flex items-center justify-between tabular-nums">
                    <span className="text-xs uppercase tracking-wider font-bold text-neutral-500">Subtotal</span>
                    <span className="text-base font-extrabold text-[#0F172A]" data-testid="new-customer-order-subtotal">OMR {subtotal.toFixed(3)}</span>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Desktop bottom action bar */}
        <div className="hidden md:flex p-4 border-t border-neutral-100 sticky bottom-0 bg-white items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
            data-testid="new-customer-order-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="text-xs font-bold text-white px-4 py-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="new-customer-order-submit-btn"
          >
            {submitting
              ? 'Creating…'
              : hasOverCap
                ? 'Fix stock warnings to submit'
                : cartRows.length > 0
                  ? `Submit · ${cartRows.length} item${cartRows.length === 1 ? '' : 's'}`
                  : 'Add items to submit'}
          </button>
        </div>

        {/* Mobile fixed bottom actions — Feb 2026 native-app pass */}
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-neutral-200 px-4 py-3 flex items-center gap-3 shadow-[0_-6px_20px_-8px_rgba(15,23,42,0.15)] pb-[max(env(safe-area-inset-bottom,0),12px)]"
          data-testid="new-customer-order-mobile-actions"
        >
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 h-12 rounded-full border border-neutral-300 text-[#0F172A] text-sm font-bold active:bg-neutral-100 disabled:opacity-50"
            data-testid="new-customer-order-cancel-btn-mobile"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="flex-1 h-12 rounded-full text-white text-sm font-bold bg-[#0078D4] active:bg-[#005A9E] disabled:opacity-40"
            data-testid="new-customer-order-submit-btn-mobile"
          >
            {submitting
              ? 'Submitting…'
              : hasOverCap
                ? 'Fix stock to submit'
                : cartRows.length > 0
                  ? `Submit · ${cartRows.length}`
                  : 'Add items'}
          </button>
        </div>
      </div>

      {/* Excel upload preview — nested modal. Shown ONLY when there is a
          parsed preview to confirm. Invalid rows are highlighted; on
          confirm the valid rows merge into the cart and the invalid rows
          are auto-skipped. Does not touch any order-creation code. */}
      {excelPreview && (
        <div
          className="absolute inset-0 bg-black/40 z-10 flex md:items-center md:justify-center md:p-4"
          data-testid="excel-import-preview-modal"
          onClick={(e) => { if (e.target === e.currentTarget) cancelExcelImport(); }}
        >
          <div className="bg-white w-full h-full md:h-auto md:rounded-2xl md:max-w-2xl md:max-h-[85vh] flex flex-col">
            <div className="p-4 md:p-5 border-b border-neutral-100 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500">
                  Preview Excel import
                </div>
                <div className="font-display text-base md:text-lg font-bold text-[#0F172A] truncate">
                  {excelPreview.fileName}
                </div>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                    data-testid="excel-import-valid-count"
                  >
                    <CheckCircle2 className="w-3 h-3" /> {excelPreview.validCount} valid
                  </span>
                  {excelPreview.invalidCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5"
                      data-testid="excel-import-invalid-count"
                    >
                      <AlertCircle className="w-3 h-3" /> {excelPreview.invalidCount} invalid — will be skipped
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={cancelExcelImport}
                className="text-neutral-400 hover:text-neutral-700 p-1 shrink-0"
                data-testid="excel-import-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-xs" data-testid="excel-import-preview-table">
                <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Row</th>
                    <th className="px-3 py-2 text-left">Item Code</th>
                    <th className="px-3 py-2 text-left">Product</th>
                    <th className="px-3 py-2 text-right">Qty</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {excelPreview.rows.map((r, i) => (
                    <tr
                      key={`${r.itemCode}-${i}`}
                      className={`border-t border-neutral-100 ${r.error ? 'bg-red-50/70' : ''}`}
                      data-testid={`excel-import-row-${i}`}
                    >
                      <td className="px-3 py-2 font-mono text-neutral-500">{r.rowNum}</td>
                      <td className="px-3 py-2 font-mono">
                        {r.itemCode || <span className="text-neutral-400">(empty)</span>}
                      </td>
                      <td className="px-3 py-2 truncate max-w-[220px]">
                        {r.product ? (
                          <span className="font-semibold text-[#0F172A]">{r.product.name}</span>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">
                        {typeof r.qty === 'number' && Number.isFinite(r.qty)
                          ? r.qty
                          : String(r.qty ?? '')}
                      </td>
                      <td className="px-3 py-2">
                        {r.error ? (
                          <span
                            className="text-red-700 font-semibold"
                            data-testid={`excel-import-row-error-${i}`}
                          >
                            {r.error}
                          </span>
                        ) : (
                          <span className="text-emerald-700 font-semibold inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Ready
                            {r.mergedRows && r.mergedRows.length > 1 && (
                              <span className="ml-1 text-[10px] font-normal text-neutral-500">
                                (merged {r.mergedRows.length} rows)
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-neutral-100 bg-white flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={cancelExcelImport}
                className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full border border-neutral-300 hover:bg-neutral-50"
                data-testid="excel-import-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmExcelImport}
                disabled={excelPreview.validCount === 0}
                className="text-xs font-bold text-white px-4 py-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-40 inline-flex items-center gap-1.5"
                data-testid="excel-import-confirm-btn"
              >
                {excelPreview.validCount === 0
                  ? 'No valid rows to add'
                  : `Add ${excelPreview.validCount} item${excelPreview.validCount === 1 ? '' : 's'} to order`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewCustomerOrderModal;
